import { useLayoutEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { CHROME_OFFSET_VAR, ZClass } from "@/lib/zLayers";

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * ChromeBannerStrip — the shell's banner row (impersonation, maintenance,
 * optimization / build-route / geocode progress), and the single place that
 * publishes the strip's height to fixed-position descendants.
 *
 * ## Why (BUG-462)
 *
 * The Orders/Routes map view renders as a `fixed inset-0` overlay, so it covers
 * the viewport starting at `top: 0` — i.e. *underneath* this strip. Its top row
 * of controls ("Показать все", the Leaflet zoom control, the KPI capsule) was
 * therefore clipped by the banner painting over it. Paint order was never the
 * problem: the banner already out-ranks the map overlay
 * ({@link ZClass.chromeBanner} 50 > {@link ZClass.mapFullscreen} 40), so raising
 * the banner would have left the controls just as hidden.
 *
 * The fix is geometric. This component measures its own rendered height and
 * writes it into the `--chrome-offset` CSS custom property on the shell column,
 * so any fixed overlay can start *below* the chrome instead of behind it. The
 * measurement is observed, not hardcoded, because the strip's height varies:
 * zero, one, or several banners may be active at once, and each wraps to two
 * lines at narrow widths / in the RU locale.
 *
 * Rendered as a plain wrapper (`display: contents` would break the sticky
 * banners' containing block, so it is a normal flex child that adds no styling
 * of its own beyond the stacking layer).
 */
export function ChromeBannerStrip({ children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Publish onto the shell column (the ancestor that also contains <main>),
    // falling back to the element itself. Writing to the document root would
    // leak the dispatcher's strip height into the admin and dealer shells,
    // which have their own.
    const target = el.parentElement ?? el;

    // A shell may render SEVERAL strips (the dispatcher puts the impersonation
    // banner above <Header /> and the progress banners below it). The offset a
    // fixed overlay needs is their SUM, so always measure every sibling strip
    // rather than just this one — otherwise the two instances would race,
    // each overwriting the other with its own height.
    const publish = () => {
      const total = [
        ...target.querySelectorAll<HTMLElement>(":scope > [data-chrome-strip]"),
      ].reduce((sum, strip) => sum + strip.offsetHeight, 0);
      target.style.setProperty(CHROME_OFFSET_VAR, `${total}px`);
    };

    publish();

    // Banners mount/unmount and reflow (progress banners appear mid-session,
    // the impersonation label wraps at narrow widths), so observe rather than
    // measuring once.
    //
    // Guarded: ResizeObserver is absent in jsdom (and in very old browsers).
    // The one-shot `publish()` above has already run, so the offset is still
    // correct for the current layout — only live re-measurement is skipped.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(publish);
    observer?.observe(el);

    return () => {
      observer?.disconnect();
      // Recompute from the strips that remain; only clear when this was the
      // last one, so unmounting one strip doesn't wipe the other's offset.
      const remaining = [
        ...target.querySelectorAll<HTMLElement>(":scope > [data-chrome-strip]"),
      ].filter((strip) => strip !== el);
      if (remaining.length === 0) {
        target.style.removeProperty(CHROME_OFFSET_VAR);
      } else {
        const total = remaining.reduce(
          (sum, strip) => sum + strip.offsetHeight,
          0,
        );
        target.style.setProperty(CHROME_OFFSET_VAR, `${total}px`);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      data-chrome-strip=""
      className={cn(
        "relative flex shrink-0 flex-col",
        ZClass.chromeBanner,
        className,
      )}
    >
      {children}
    </div>
  );
}
